import React from 'react';

import { useClassnames, visuallyHidden } from '@lumapps/classnames';
import { TextList } from '@lumapps/lumx-texts/components/TextList';
import { Text, HeadingElement } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { AskAiSource } from '../../types';
import { AskAiSourceChip } from '../AskAiSourceChip';
import { AskAiSourceListMoreChip } from './AskAiSourceListMoreChip';

import './index.scss';

export interface AskAiSourcesListProps {
    /** List of sources to display in the component */
    sources: AskAiSource[];
    /** Determines if the sources list should be displayed vertically */
    isVertical?: boolean;
    /** Heading level for the sources list */
    headingLevel?: HeadingElement;
    /** A custom className to set on the wrapper */
    className?: string;
    /** Callback triggered when a source is clicked */
    onSourceClick?: (source: AskAiSource) => void;
}

const CLASSNAME = 'ask-ai-sources-list';

/**
 * AskAiSourcesList component displays a list of sources used to generate an Ask AI response.
 * It renders the sources as a list of chips, which can be displayed horizontally or vertically.
 *
 * @param AskAiSourcesListProps The props for the AskAiSourcesList component.
 * @returns AskAiSourcesList
 */
export const AskAiSourcesList: React.FC<AskAiSourcesListProps> = ({
    className,
    sources,
    isVertical = false,
    headingLevel = 'h4',
    onSourceClick,
}) => {
    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);

    const [displayExtraSources, setDisplayExtraSources] = React.useState(false);

    const hasMoreThan3Sources = sources.length > 3;
    const defaultSources = sources.slice(0, 3);
    const extraSources = sources.length > 3 ? sources.slice(3, sources.length) : undefined;
    const sourceChipClassName = 'source-chip';

    const renderSources = (sources: AskAiSource[]) =>
        sources.map((source) => (
            <li key={source.id} className={element(sourceChipClassName)}>
                <AskAiSourceChip source={source} onClick={onSourceClick && (() => onSourceClick(source))} />
            </li>
        ));

    return (
        <div className={block(undefined, [className])}>
            <Text as={headingLevel} className={visuallyHidden()}>
                {translateKey(GLOBAL.SOURCES)}
            </Text>
            <TextList
                as="ul"
                className={element('list', {
                    'has-more': hasMoreThan3Sources && !displayExtraSources,
                    vertical: isVertical,
                })}
            >
                {renderSources(defaultSources)}
                {hasMoreThan3Sources && extraSources && !displayExtraSources && (
                    <AskAiSourceListMoreChip
                        className={element(sourceChipClassName)}
                        remainingSourcesCount={String(extraSources?.length)}
                        setDisplayExtraSources={setDisplayExtraSources}
                    />
                )}
                {displayExtraSources && extraSources && renderSources(extraSources)}
            </TextList>
        </div>
    );
};
